import { chat as llmChat } from './llm-client';
import { loadRagChunks, loadKgFull, loadBenchmarkComparisons, loadTermDictionary, loadDomainConfig } from './data-loader';
import { buildBenchmarkContext, benchmarkPageRef } from './benchmark-context';
import { runQueryPipeline, structuredMatches } from './query-engine';
import { spellCorrectQuery } from './spell-correct';
import { GRASP_DEFAULTS } from '../DomainContext';
import { buildMethodSummaries, formatRagContext, DEFAULT_SUMMARY_COLUMNS, DEFAULT_SHORT_NAMES } from './copilot/rag-context';
import { retrieveChunks } from './copilot/retrieval';
import { buildKgContext } from './copilot/kg-context';
import { computeCorpusFacts } from './copilot/corpus-facts';
import { buildAnswerPrompt } from './copilot/prompt-builder';
import { rankCandidates, parseStructuredAnswer, resolveMethods, salvageAnswer } from './copilot/answer-synthesis';

// Deterministic FORMAT intent from the query (mirrors the answer-engine router):
// chooses the answer's shape (table vs ranked list vs bullets) before generation.
export function classifyFormatIntent(q) {
  const s = (q || '').toLowerCase();
  // OVERVIEW / LANDSCAPE — "give an overview of the methods", "the landscape",
  // "all methods", "what approaches are there", "survey". Must survey the WHOLE
  // set (grouped), not pick a couple — checked FIRST so it wins over the others.
  if (/\b(overview|landscape|survey|taxonomy|summar\w*|the field|the literature|all (the |available )?(methods|approaches|algorithms|techniques)|(list|show) (me )?(all|the|every|methods|approaches|algorithms)|what (methods|approaches|algorithms) (are|exist))\b/.test(s)) return 'overview';
  // COMPARISON — explicit compare words, OR comparative-performance phrasing that
  // implies a head-to-head: "do X perform better in ...", "A better than B",
  // "which is better", "A or B" between two named things.
  if (/\b(vs\.?|versus|compared?|comparison|difference between|differ\w*|trade-?offs?|better than|worse than|perform\w*\s+(?:better|worse)|which\b[^?]*\b(?:better|worse))\b/.test(s)) return 'comparison';
  if (/\b(best|fastest|highest|top|rank(ing)?|outperform\w*|state[- ]of[- ]the[- ]art|sota|success rate|accuracy|most accurate|perform\w* best|better|worse)\b/.test(s)) return 'ranking';
  if (/\b(which|recommend|methods? for|approaches? for|list|options?|suitable|suited|good for|use for)\b/.test(s)) return 'recommendation';
  return 'default';
}

// buildMethodSummaries is re-exported for backward compatibility — a test imports
// it from ./ai-pipeline; the implementation now lives in ./copilot/rag-context.
export { buildMethodSummaries };

// Compact a name/id to bare alphanumerics (drops the 🤖 prefix, case, punctuation)
// so "🤖 GraspQP", "graspQP" and paper_id "graspqp" all reduce to "graspqp".
function compactName(s) {
  return String(s || '').replace(/^[^\p{L}\p{N}]+/u, '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/**
 * resolveMethodPaperId(methodName, kgData) -> the paper id a method is
 * described_in, via the KG's method->described_in->paper edge (precise), or
 * null when the method node / edge doesn't resolve. Precise resolution avoids
 * the compact-substring bug where "GraspNet" mis-matched "contact-graspnet".
 */
function resolveMethodPaperId(methodName, kgData) {
  if (!kgData || !Array.isArray(kgData.nodes) || !kgData.nodes.length) return null;
  const wanted = String(methodName || '').toLowerCase().trim();
  if (!wanted) return null;
  const sidOf = (l) => (l.source && l.source.id) || l.source;
  const tidOf = (l) => (l.target && l.target.id) || l.target;
  const methodNode = kgData.nodes.find(n => n.type === 'method' && (n.label || '').toLowerCase().trim() === wanted);
  if (!methodNode) return null;
  const paperLink = (kgData.links || []).find(l => l.type === 'described_in' && sidOf(l) === methodNode.id);
  if (!paperLink) return null;
  const paperNode = kgData.nodes.find(n => n.id === tidOf(paperLink));
  return (paperNode && (paperNode.paper_id || paperNode.id)) || null;
}

/**
 * methodsNamedInQuery(query, allMethods) -> the methods the user EXPLICITLY named.
 * A method someone asks to compare must always get evidence + a candidate slot,
 * even if semantic retrieval ranks its chunks below the top-K (the GraspQP bug:
 * "compare graspQP to graspVLA" dropped GraspQP because GraspVLA's chunks won
 * retrieval). Single-word names match as a whole query token (so "GraspQP" hits
 * but "grasp" inside "graspqp" does not); multi-word names match as a substring.
 */
export function methodsNamedInQuery(query, allMethods) {
  const qLc = ` ${String(query || '').toLowerCase()} `;
  const qTokens = new Set(qLc.match(/[a-z0-9]+/g) || []);
  const out = [];
  for (const m of (allMethods || [])) {
    const nameLc = String(m.name || '').replace(/^[^\p{L}\p{N}]+/u, '').trim().toLowerCase();
    if (!nameLc) continue;
    // Aliases the user might type: the full name, any trailing "(acronym)" like
    // "(VGN)"/"(GIGA)", and the pre-parenthesis name ("Dex-Net 2.0"). So a method
    // written "Volumetric Grasping Network (VGN)" is found by "VGN" too.
    const aliases = new Set([nameLc]);
    const paren = nameLc.match(/\(([^)]+)\)/g) || [];
    paren.forEach(p => aliases.add(p.replace(/[()]/g, '').trim()));
    aliases.add(nameLc.replace(/\s*\([^)]*\)\s*/g, ' ').trim());
    const hit = [...aliases].some(a => {
      if (!a) return false;
      const compact = a.replace(/[^a-z0-9]+/g, '');
      const singleWord = !/[^a-z0-9]/.test(a);
      return (singleWord && compact.length >= 3 && qTokens.has(compact))
          || (a.length >= 5 && qLc.includes(` ${a} `));
    });
    if (hit) out.push(m);
  }
  return out;
}

// ── Conversational follow-up rewriting ──
// A follow-up like "what about real-world scenes?" or "which of those is
// faster?" carries a pronoun/ellipsis with nothing for retrieval to anchor to —
// it retrieves on the bare words alone. Anchoring it to the previous turn's
// discussed methods before retrieval gives BM25/cosine something concrete.
//
// There's no separate history-log field for "discussed method names" threaded
// from the caller — the previous ANSWER prose already bolds every method it
// discussed (the prompt contract requires it), so that's the source of truth
// we reconstruct from here.
export function extractDiscussedNames(answerText, limit = 4) {
  const out = [];
  const seen = new Set();
  const re = /\*\*([^*]{2,80})\*\*/g;
  let m;
  while ((m = re.exec(String(answerText || ''))) !== null) {
    const name = m[1].trim();
    const key = name.toLowerCase();
    if (name && !seen.has(key)) { seen.add(key); out.push(name); }
    if (out.length >= limit) break;
  }
  return out;
}

const FOLLOWUP_PRONOUN_RE = /\b(it|its|it's|they|their|them|those|these|that one|the first one|the second one|the last one)\b/i;
const FOLLOWUP_LEAD_RE = /^\s*(what about|and\b|how about)/i;

/**
 * rewriteFollowupQuery(query, history) -> the query, anchored to the previous
 * turn's discussed methods/topic when it reads as a pronoun/ellipsis follow-up.
 * Otherwise returns the query unchanged. Heuristic string rewrite (no LLM call).
 */
export function rewriteFollowupQuery(query, history) {
  const q = String(query || '');
  if (!history || !history.prevQuery) return q;
  const isFollowup = FOLLOWUP_PRONOUN_RE.test(q) || FOLLOWUP_LEAD_RE.test(q);
  if (!isFollowup) return q;
  const names = extractDiscussedNames(history.prevAnswer, 3);
  const topic = names.length ? names.join(' and ') : history.prevQuery;
  if (FOLLOWUP_PRONOUN_RE.test(q)) return q.replace(FOLLOWUP_PRONOUN_RE, topic);
  return `${q} (regarding ${topic})`;
}

// ── Determinism guardrail: per-session answer cache ──
// A repeated query returns the IDENTICAL answer without re-calling the LLM. Keyed
// by the spell-corrected, normalized query + a corpus fingerprint, so it
// invalidates automatically if the method set (corpus) changes. Combined with
// temperature:0, this makes "the same question, again and again → the same answer"
// hold, and near-identical phrasings that normalize/spell-correct to the same
// query share one answer. Cross-reload consistency relies on temperature:0.
const _answerCache = new Map();
const _ANSWER_CACHE_MAX = 100;
export function answerCacheKey(effectiveQuery, allMethods, history, domainMeta) {
  const q = String(effectiveQuery || '').toLowerCase().trim().replace(/\s+/g, ' ').replace(/[?.!]+$/, '');
  const list = Array.isArray(allMethods) ? allMethods : [];
  // Corpus fingerprint = a hash over ALL method names (length+first-name alone
  // survived corpus content changes and returned stale cached answers).
  let h = 5381;
  const names = list.map(m => (m && m.name) || '').join('|');
  for (let i = 0; i < names.length; i++) h = ((h * 33) ^ names.charCodeAt(i)) >>> 0;
  const fp = `${list.length}:${h.toString(36)}`;
  // A follow-up asked in a different conversation context is a different answer.
  const hist = history && history.prevQuery ? `::after:${String(history.prevQuery).toLowerCase().trim()}` : '';
  // Domain + a build-time signal (when the domain config carries one) — a
  // domain switch OR a fresh data rebuild invalidates the cache even when the
  // method COUNT+names hash happens to coincide (e.g. a re-extraction that
  // changed benchmark/KG data but not the method roster).
  const dm = domainMeta || {};
  const domainPart = dm.domain || dm.productSubject || '';
  const buildPart = dm.buildTimestamp || '';
  return `${fp}::${domainPart}::${buildPart}::${q}${hist}`;
}
export function _clearAnswerCache() { _answerCache.clear(); }

/**
 * findUngroundedNumbers(answer, contextBlob) — every quantitative figure in the
 * answer must literally appear in the evidence the model was given. Returns the
 * figures that don't (conservatively: years and small integers ≤10 are exempt —
 * counts like "3 methods" are the answer's own arithmetic, not evidence claims).
 * This turns the grounding check from telemetry into ENFORCEMENT: fabricated
 * numbers get a visible caveat instead of passing silently with a [P#] tag.
 */
export function findUngroundedNumbers(answer, contextBlob) {
  const nums = (String(answer || '').match(/\d+(?:\.\d+)?/g) || []).filter(n => {
    const v = parseFloat(n);
    if (v >= 1900 && v <= 2100 && Number.isInteger(v)) return false;  // years
    if (Number.isInteger(v) && v <= 10) return false;                 // small counts
    return true;
  });
  const ctx = String(contextBlob || '');
  const out = [];
  for (const n of [...new Set(nums)]) {
    const alt = n.includes('.') ? n.replace(/0+$/, '').replace(/\.$/, '') : `${n}.0`;
    if (!ctx.includes(n) && !ctx.includes(alt)) out.push(n);
  }
  return out;
}

/**
 * composeDeterministicAnswer(intent, benchmarkText, corpusFacts, structuredText,
 * candMethods) -> a markdown answer body rendered ENTIRELY from what was already
 * computed deterministically (no LLM), for when the LLM call fails outright
 * after its retry. Returns '' when there is nothing deterministic to show.
 */
export function composeDeterministicAnswer(intent, benchmarkText, corpusFacts, structuredText, candMethods) {
  const parts = [];
  if (corpusFacts) parts.push(`**What the corpus data shows:**\n${corpusFacts}`);
  if (structuredText) parts.push(`**Methods matching your query's attributes:**\n${structuredText}`);
  if (benchmarkText && !/^No leaderboard/.test(benchmarkText)) parts.push(`**Verified benchmark values:**\n${benchmarkText}`);
  if (!parts.length && Array.isArray(candMethods) && candMethods.length) {
    parts.push(`**Candidate methods for this query:**\n${candMethods.slice(0, 12).map(m => `- ${m.name}`).join('\n')}`);
  }
  if (!parts.length) return '';
  return `The AI summary is temporarily unavailable — here is what the data itself answers (generated without AI synthesis):\n\n${parts.join('\n\n')}`;
}

function runGroundingCheck(insightText, methods, kgNodes) {
  const grounded = [];
  const ungrounded = [];

  const allKnown = new Set();
  methods.forEach(m => allKnown.add(m.name.replace(/\u{1F916}\s*/gu, '').trim().toLowerCase()));
  if (kgNodes) {
    kgNodes.forEach(n => {
      if (n.label) allKnown.add(n.label.toLowerCase());
    });
  }

  const mentions = new Set();
  // CamelCase or hyphenated method names
  const camelRe = /\b([A-Z][a-z]+(?:[-]?[A-Z][a-z]+)+(?:\+\+)?(?:\s*\([^)]+\))?)\b/g;
  let match;
  while ((match = camelRe.exec(insightText)) !== null) mentions.add(match[1]);
  // ALLCAPS acronyms
  const acroRe = /\b([A-Z]{2,6}(?:-[A-Z]+)?)\b/g;
  const skip = new Set(['RGB', 'RGBD', 'CNN', 'GNN', 'MLP', 'LLM', 'DOF', 'SAM', 'GPU',
    'CPU', 'PDF', 'API', 'IOU', 'MAP', 'BCE', 'MSE', 'SGD', 'IEEE',
    'ICRA', 'IROS', 'RSS', 'CVPR', 'ICLR', 'RAG', 'KG', 'HGT', 'RL', 'IL', 'SE', 'TSDF', 'BPS', 'FPS', 'NMS']);
  while ((match = acroRe.exec(insightText)) !== null) {
    if (!skip.has(match[1])) mentions.add(match[1]);
  }

  // Whole-word matching so "GraspNet" is no longer counted as grounded merely
  // because some known label contains "Net" (the old loose substring test).
  const knownPadded = [...allKnown].filter(k => k.length >= 4)
    .map(k => ' ' + k.replace(/[^a-z0-9]+/g, ' ').trim() + ' ');
  for (const mention of mentions) {
    const lower = mention.toLowerCase();
    const lw = ' ' + lower.replace(/[^a-z0-9]+/g, ' ').trim() + ' ';
    const found = knownPadded.some(kw => lw.includes(kw) || (lower.length >= 5 && kw.includes(lw)));
    if (found) grounded.push(mention);
    else ungrounded.push(mention);
  }

  return { grounded, ungrounded };
}

export async function runAIQuery(query, allMethods, queryKeywords, domainOpts = {}) {
  const DEFAULT_WEIGHTS = domainOpts.defaultWeights || GRASP_DEFAULTS.defaultWeights;
  const domainBranding = domainOpts.branding || GRASP_DEFAULTS.branding;
  // Defensive: the contract is "never throws, always returns the documented
  // shape", so normalize a nullish allMethods before the pre-pipeline map/filter
  // (which sit outside the per-step try/catches) can throw.
  allMethods = Array.isArray(allMethods) ? allMethods : [];
  // Step 0: Spell correction
  const methodNames = allMethods.map(m => m.name);
  const { text: correctedQuery, corrected: wasSpellCorrected } = await spellCorrectQuery(query, queryKeywords, methodNames);
  // Follow-up rewrite: a pronoun/ellipsis query ("what about it?") is anchored to
  // the previous turn's discussed methods BEFORE retrieval, so BM25/cosine have
  // something concrete to match. Kept separate from `correctedQuery` so the
  // spell-correction UI (`spellCorrection.corrected`) never shows this internal
  // augmentation to the user.
  const effectiveQuery = rewriteFollowupQuery(correctedQuery, domainOpts.history);
  // FORMAT intent, computed early so candidate selection can widen for an
  // OVERVIEW/landscape query (which must survey the whole set, not a few).
  const intent = classifyFormatIntent(effectiveQuery);

  // Domain metadata for the cache fingerprint (Step below) — best-effort; a
  // missing/failed domain-config load just means the domain/build components
  // of the key are empty, not a broken cache.
  let domainConfig = null;
  try { domainConfig = await loadDomainConfig(); } catch (e) { /* optional */ }

  // Determinism guardrail: an identical (normalized) query returns the cached
  // answer verbatim and skips the LLM entirely.
  const onStage = typeof domainOpts.onStage === 'function' ? domainOpts.onStage : () => {};
  const _ck = answerCacheKey(effectiveQuery, allMethods, domainOpts.history, {
    domain: domainConfig?.domain, productSubject: domainBranding.productSubject,
    buildTimestamp: domainConfig?.buildTimestamp || domainConfig?.built_at || domainConfig?.generated_at,
  });
  if (_answerCache.has(_ck)) { onStage('done'); return _answerCache.get(_ck); }
  onStage('retrieving');

  // Step 1: Deterministic pipeline
  const { weights: newWeights, colorBy: newColorBy, filterMethods } =
    runQueryPipeline(effectiveQuery, DEFAULT_WEIGHTS, allMethods, queryKeywords);

  // Step 2: Resolve the query's method subset for highlighting/filtering.
  // The copilot does NO clustering — it neither re-clusters nor reasons over
  // clusters. The scatter keeps its default HDBSCAN colours and layout (clustering
  // is an Explorer concern, not a copilot one), so each method passes through with
  // its existing `cluster`/x/y untouched.
  const subset = filterMethods
    ? allMethods.filter(m => filterMethods.includes(m.name))
    : allMethods;
  const responseData = subset.length ? subset : allMethods;

  // Pre-synthesis default highlight set (overridden after synthesis by the
  // methods the model actually discusses — see finalHighlight below).
  const highlightMethods = filterMethods || responseData.slice(0, 8).map(m => m.name);

  // Step 3: RAG retrieval (client-side). Tag each retrieved PAPER with a stable
  // [P#] id so the answer can cite it inline and the UI can render a chip.
  // kgData + termDictionary are loaded HERE (not at Step 5) so the pinned-method
  // paper resolution below can use the KG's method->described_in->paper edge
  // instead of guessing from a compact-substring match, and so BM25 gets the
  // domain-dictionary synonym expansion.
  let ragText = '';
  let ragCitations = [];
  let ragAnalytics = {};
  let papersById = [];
  let kgData = null;
  try {
    const [ragChunks, kgDataLoaded, termDictionary] = await Promise.all([
      loadRagChunks(),
      loadKgFull().catch(() => null),
      loadTermDictionary().catch(() => null),
    ]);
    kgData = kgDataLoaded;
    if (ragChunks.length) {
      const scored = await retrieveChunks(effectiveQuery, ragChunks, {
        termDictionary, attributeTerms: queryKeywords?.attributeTerms,
      });
      ragCitations = formatRagContext(scored).ragCitations;
      const seen = new Map();
      scored.forEach(chunk => {
        const pid = chunk.metadata?.paper_id || chunk.metadata?.paper_title || 'unknown';
        if (!seen.has(pid) && seen.size < 6) {
          seen.set(pid, {
            tag: `P${seen.size + 1}`, paper_id: pid,
            paper_title: chunk.metadata?.paper_title || pid, chunks: [],
          });
        }
        if (seen.has(pid)) seen.get(pid).chunks.push(chunk);
      });

      // PIN methods the user explicitly named: guarantee their paper is in the
      // evidence even if semantic retrieval ranked its chunks below the top-6, so
      // the copilot can cite BOTH sides of a comparison. (Root fix for the GraspQP
      // bug — its 73 chunks existed but lost retrieval to GraspVLA.)
      const namedMethods = methodsNamedInQuery(effectiveQuery, allMethods);
      const qTokens = new Set((effectiveQuery.toLowerCase().match(/[a-z0-9]+/g) || []).filter(t => t.length > 2));
      namedMethods.forEach(m => {
        const mc = compactName(m.name);
        if (!mc) return;
        // Resolve method->paper via the KG's described_in edge when possible
        // (precise); only fall back to compact-substring matching — now guarded
        // by a length-ratio check — when no edge resolves (previously an
        // open-ended startsWith mis-linked "GraspNet" to "contact-graspnet").
        const kgPaperId = resolveMethodPaperId(m.name, kgData);
        const kgPc = kgPaperId ? compactName(kgPaperId) : null;
        const matchesPaperId = (pid) => {
          const pc = compactName(pid);
          if (!pc) return false;
          if (kgPc) return pc === kgPc;
          if (pc === mc) return true;
          return Math.abs(pc.length - mc.length) <= 3 && (pc.startsWith(mc) || mc.startsWith(pc));
        };
        const covered = [...seen.values()].some(p => matchesPaperId(p.paper_id));
        if (covered) return;
        const mChunks = ragChunks.filter(ch => matchesPaperId(ch.metadata?.paper_id));
        if (!mChunks.length) return;
        // Take the chunks with the most query-token overlap (best support for the ask).
        const best = mChunks
          .map(ch => {
            const words = new Set((ch.text || '').toLowerCase().match(/[a-z0-9]+/g) || []);
            let ov = 0; qTokens.forEach(t => { if (words.has(t)) ov++; });
            return { ch, ov };
          })
          .sort((a, b) => b.ov - a.ov)
          .slice(0, 3)
          .map(x => x.ch);
        const pid = mChunks[0].metadata?.paper_id || mc;
        seen.set(pid, {
          tag: `P${seen.size + 1}`, paper_id: pid,
          paper_title: mChunks[0].metadata?.paper_title || pid, chunks: best,
          pinned: true,
        });
        // Surface the pinned chunks as citations too (so the citation modal has them).
        ragCitations = ragCitations.concat(formatRagContext(best).ragCitations);
      });

      papersById = [...seen.values()];
      // Per-CHUNK budget, not a fixed per-paper total: the first (best-ranked)
      // chunk keeps 1200 chars, every subsequent chunk keeps 600 — so a paper
      // with 3 supporting chunks gets ~2400 chars of evidence instead of being
      // capped at a flat 1600 regardless of how much was retrieved for it.
      ragText = papersById.map(p => {
        // Cap at 3 chunks/paper too: 1200 + 600 + 600 ≈ the 2400-char budget.
        const text = p.chunks.slice(0, 3).map((c, i) => (c.text || '').slice(0, i === 0 ? 1200 : 600)).join(' … ');
        return `[${p.tag}] ${p.paper_title}\n${text}`;
      }).join('\n\n');
      ragAnalytics = { papers: [...new Set(papersById.map(p => p.paper_id).filter(Boolean))], totalChunks: scored.length };
    }
  } catch (e) { console.warn('RAG retrieval failed:', e); }

  // Step 4: Candidate shortlist — rank methods by query relevance (RAG papers +
  // deterministic filter + name/keyword overlap) so the model chooses from
  // RELEVANT methods, not array order. Each carries a stable id used end-to-end.
  const COLS = (domainOpts.summaryColumns && domainOpts.summaryColumns.length) ? domainOpts.summaryColumns : DEFAULT_SUMMARY_COLUMNS;
  const SHORTS = domainOpts.shortNames || DEFAULT_SHORT_NAMES;
  const ranked = rankCandidates(allMethods, {
    filterMethods: filterMethods || [], ragPapers: ragAnalytics.papers || [],
    ragCitations, query: effectiveQuery,
  });
  // Overview/landscape queries survey the whole set, so give the model a much
  // wider candidate list (all/most methods) instead of the top-14 for a focused query.
  const candCap = intent === 'overview' ? 40 : 14;
  let candMethods = ranked.slice(0, candCap).map(name => allMethods.find(m => m.name === name)).filter(Boolean);
  // Force every method the user NAMED into the candidate set (front), even if it
  // ranked below the cap — so its metadata is always in the candidate block and the
  // model can fill each dimension instead of writing "Not specified".
  const namedForCand = methodsNamedInQuery(effectiveQuery, allMethods);
  if (namedForCand.length) {
    const have = new Set(candMethods.map(m => m.name));
    candMethods = [...namedForCand.filter(m => !have.has(m.name)), ...candMethods];
  }
  const fmtCandidate = (m) => {
    const parts = COLS.map(c => { const v = m.metadata?.[c]; return v ? `${SHORTS[c] || c}=${v}` : null; }).filter(Boolean);
    return `- ${m.name}${parts.length ? ' — ' + parts.join('; ') : ''}`;
  };
  let candidateBlock = candMethods.map(fmtCandidate).join('\n');

  // Step 5: KG context — verbalized relations (no arrow logs), seeded from the
  // candidates + RAG papers. Supplements the paper text; never the sole source.
  // kgData was already loaded in Step 3 (for the pinned-method resolution above)
  // — reused here rather than re-fetched.
  let kgContext = '';
  let kgTraversal = [];
  try {
    if (kgData && Array.isArray(kgData.nodes) && kgData.nodes.length) {
      const kg = buildKgContext(kgData, candMethods.slice(0, 5).map(m => m.name), { seedPaperIds: ragAnalytics.papers || [] });
      kgContext = kg.kgContext;
      kgTraversal = kg.kgTraversal;
    }
  } catch (e) { console.warn('KG context failed:', e); }

  onStage('grounding');
  // Step 6: Benchmark grounding for quantitative/ranking queries. Each block is
  // tagged [B#] so the prompt's "[B#] citation" instruction actually has a target
  // (previously the model was told to cite [B#] against an untagged block).
  let benchmarkText = '';
  let bmPageRef = null;
  let benchData = null;
  let benchTags = [];
  try {
    benchData = await loadBenchmarkComparisons();
    const rawBench = buildBenchmarkContext(effectiveQuery, benchData, { knownMethods: methodNames });
    if (rawBench && !/^No leaderboard/.test(rawBench)) {
      const blocks = rawBench.split('\n\n').filter(Boolean);
      benchmarkText = blocks.map((b, i) => `[B${i + 1}] ${b}`).join('\n\n');
      benchTags = blocks.map((b, i) => ({
        marker: `B${i + 1}`,
        title: (b.split('\n')[0] || '').replace(/\s*\(values measured.*$/, '').trim(),
      }));
    } else {
      benchmarkText = rawBench;
    }
    bmPageRef = benchmarkPageRef(effectiveQuery, benchData, { knownMethods: methodNames, methods: allMethods });
  } catch (e) { /* benchmarks optional */ }

  // Step 6b: Corpus-level derived facts (top-cited, most-connected, newest,
  // most-benchmarked) the LLM can't infer from prose alone — so a query like
  // "the top-cited method" resolves to a real, named item instead of "not
  // specified". Computed from the already-loaded KG + methods + benchmarks.
  let corpusFacts = '';
  try {
    const factObj = computeCorpusFacts({ methods: allMethods, kg: kgData, benchmarks: benchData });
    corpusFacts = factObj.factsText;
    // Make every fact-named method DISCUSSABLE: add any that aren't already
    // candidates, so the model can answer about "the top-cited method" (not just
    // name it). Then rebuild the candidate block.
    const factNames = Object.values(factObj.facts || {}).map(f => f && f.name).filter(Boolean);
    let added = false;
    factNames.forEach(name => {
      if (!candMethods.find(m => m.name === name)) {
        const m = allMethods.find(mm => mm.name === name);
        if (m) { candMethods.push(m); added = true; }
      }
    });
    if (added) candidateBlock = candMethods.map(fmtCandidate).join('\n');
  } catch (e) { /* facts optional */ }

  // Step 6c: Structured attribute matches — the EXACT methods whose metadata
  // matches each attribute the question names (Hardware=Suction, Scene=Piled, …).
  // The Explorer table's attribute filter is exhaustive but RAG/LLM answer
  // selection is NOT, so without this a matching method (e.g. a suction method)
  // can be silently dropped from the answer. Inject the per-attribute matches AND
  // add them to the candidates, so the model addresses every relevant one — or
  // says why one is out of scope — instead of omitting it.
  let structuredText = '';
  try {
    const matches = structuredMatches(effectiveQuery, allMethods, queryKeywords?.attributeTerms || {});
    if (matches.length) {
      structuredText = matches.map(mm => `- ${mm.col} = ${mm.value}: ${mm.methods.join(', ')}`).join('\n');
      const toAdd = [...new Set(matches.flatMap(mm => mm.methods))].slice(0, 12);
      let added = false;
      toAdd.forEach(name => {
        if (!candMethods.find(m => m.name === name)) {
          const m = allMethods.find(mm2 => mm2.name === name);
          if (m) { candMethods.push(m); added = true; }
        }
      });
      if (added) candidateBlock = candMethods.map(fmtCandidate).join('\n');
    }
  } catch (e) { /* structured matches optional */ }

  // Step 7: SINGLE structured synthesis — answer markdown + the methods it
  // discusses (by id) + citations, in ONE JSON object. The discussed ids are the
  // SOLE selector for the comparison table, so prose and table cannot diverge.
  // (intent was computed early, above, so candidate selection could widen for overview.)
  onStage('writing');
  let parsed = null;
  let insightText = '';
  const history = domainOpts.history || null;
  // Overview answers enumerate the whole candidate set — give them the budget to
  // finish. Budgets include headroom for gpt-oss REASONING tokens, which count against
  // max_tokens before any visible answer is produced; too small a budget truncates the
  // JSON mid-answer (which then can't parse). Kept under api/chat.js MAX_TOKENS_CAP.
  // Budgets are deliberately GENEROUS so answers always COMPLETE: an overview enumerates
  // the whole corpus (dozens of methods), so it needs the most room, and on Groq's gpt-oss
  // the hidden reasoning is spent from this budget before any answer text appears.
  const tokenBudget = intent === 'overview' ? 12000 : 8000;
  for (let attempt = 0; attempt < 2 && !insightText; attempt++) {
    try {
      const { system, user } = buildAnswerPrompt({
        query: effectiveQuery, ragText, kgContext, benchmarkText, corpusFacts,
        structuredMatches: structuredText, candidateBlock, intent, branding: domainBranding,
        history,
      });
      const msgs = [{ role: 'system', content: system }, { role: 'user', content: user }];
      // NO response_format json_object: gpt-oss reasoning models hard-400 it
      // (json_validate_failed with an empty generation) whenever reasoning eats
      // the budget, which made EVERY query fail its first call and doubled load
      // (the cause of the "AI summary temporarily unavailable" fallbacks).
      // parseStructuredAnswer is robust to fenced/loose JSON, so plain mode is fine.
      const raw = await llmChat(msgs, { maxTokens: tokenBudget, temperature: 0 });
      parsed = parseStructuredAnswer(raw);
      // Fallback ONLY when there is no recoverable structured answer. Never surface a raw
      // JSON envelope: try one more salvage of the answer field, and if the leftover still
      // looks like JSON (no answer to recover), drop it rather than dumping {"answer":"... .
      const cleaned = String(raw || '').replace(/^```(?:json|markdown)?\s*/i, '').replace(/```\s*$/i, '').trim();
      insightText = parsed
        ? parsed.answer
        : (salvageAnswer(cleaned) || (/^\s*[{[]/.test(cleaned) ? '' : cleaned));
    } catch (llmErr) {
      if (attempt === 0) {
        // transient failure (429/timeout) — one retry after a short backoff
        await new Promise(r => setTimeout(r, 900));
        continue;
      }
      // DEGRADED ANSWER, not an apology: everything below was computed
      // deterministically (no LLM involved), so serve it.
      const composed = composeDeterministicAnswer(intent, benchmarkText, corpusFacts, structuredText, candMethods);
      insightText = composed || `The copilot is temporarily unavailable (${llmErr.message}). ${responseData.length} methods are shown below.`;
    }
  }

  // If both attempts produced no usable prose (e.g. a response truncated before the answer
  // field even began), serve the deterministic answer rather than rendering an empty box.
  if (!insightText || !insightText.trim()) {
    insightText = composeDeterministicAnswer(intent, benchmarkText, corpusFacts, structuredText, candMethods)
      || `${responseData.length} methods are shown below.`;
  }

  // ENFORCE numeric grounding: any figure in the answer that does not literally
  // appear in the supplied evidence gets a visible caveat (fabricated numbers
  // must never pass silently just because a [P#] tag sits next to them).
  let ungroundedNumbers = [];
  if (parsed && insightText) {
    try {
      const ctxBlob = [ragText, benchmarkText, corpusFacts, structuredText, candidateBlock, kgContext].join('\n');
      ungroundedNumbers = findUngroundedNumbers(insightText, ctxBlob);
      if (ungroundedNumbers.length) {
        insightText += `\n\n⚠ Verification note: the figure${ungroundedNumbers.length > 1 ? 's' : ''} ${ungroundedNumbers.slice(0, 4).join(', ')} could not be matched to the retrieved sources — treat ${ungroundedNumbers.length > 1 ? 'them' : 'it'} with caution.`;
      }
    } catch (e) { /* enforcement optional */ }
  }

  // Resolve the discussed method NAMES against the candidate set (fuzzy, tolerant
  // of minor renames). Fall back to the top-ranked candidates. This is the SOLE
  // selector for the comparison table, so prose and table stay in lock-step.
  const discussedRaw = parsed ? (parsed.discussed || []).map(d => d.id) : [];
  let discussedNames = resolveMethods(discussedRaw, candMethods);
  if (discussedNames.length < 2) {
    discussedNames = candMethods.slice(0, 4).map(m => m.name);
  }
  const discussed = discussedNames.map(name => ({ name }));

  // Citations the answer renders as chips: the [P#]-tagged papers (ground truth).
  // Each carries the ACTUAL retrieved chunks the LLM saw, so a citation click can
  // show the passage that supports the specific claim (not just the paper's top
  // query chunk) — on any page, with no dependency on a page-specific panel.
  const citations = papersById.map((p, i) => ({
    marker: p.tag, paper_id: p.paper_id, paper_title: p.paper_title, index: i + 1,
    chunks: (p.chunks || []).map(c => ({
      text: c.text || '',
      section: (c.metadata && c.metadata.section) || '',
      page: (c.metadata && c.metadata.page) || null,
      score: c.score || 0,
    })),
  }));
  // Benchmark blocks are citable too — a [B#] chip resolves to its verified
  // benchmark table (the chunk shows the exact grounded lines the model saw).
  benchTags.forEach((bt, i) => {
    const block = (benchmarkText.split('\n\n')[i] || '').replace(/^\[B\d+\]\s*/, '');
    citations.push({
      marker: bt.marker, paper_id: null, paper_title: `Verified benchmark: ${bt.title}`,
      index: papersById.length + i + 1, benchmark: true,
      chunks: block ? [{ text: block, section: 'verified benchmarks', page: null, score: 1 }] : [],
    });
  });

  // Grounding guardrail on the ANSWER (surfaced in the provenance drawer, not the body).
  let grounding = { grounded: [], ungrounded: [] };
  try {
    grounding = runGroundingCheck(insightText, allMethods, (kgData && kgData.nodes) || []);
  } catch (e) {}
  grounding.ungroundedNumbers = ungroundedNumbers;

  // methodRelevance: the discussed methods, in order — the SINGLE source the
  // comparison table + chips read, so they match the prose exactly.
  const methodRelevance = discussed.map((d, i) => ({
    name: d.name, score: Math.max(0.5, 1 - i * 0.08), why: d.why,
  }));
  const finalHighlight = discussedNames.length ? discussedNames : highlightMethods;

  const result = {
    success: true,
    umapData: responseData,
    weights: newWeights,
    colorBy: newColorBy,
    filterMethods,
    highlightMethods: finalHighlight,
    insight: insightText,
    grounding,
    ragCitations,
    ragAnalytics,
    citations,
    benchmarkPageRef: bmPageRef,
    methodRelevance,
    paperRelevance: methodRelevance,
    kgContext,
    kgTraversal,
    intent,
    // `correctedQuery`, NOT `effectiveQuery` — the follow-up rewrite is an
    // internal retrieval aid and must never appear in the "did you mean" UI.
    spellCorrection: wasSpellCorrected ? { original: query, corrected: correctedQuery } : null,
  };
  // Cache successful answers only (don't pin an LLM-unavailable fallback). Simple
  // FIFO cap so a long session can't grow the cache unbounded.
  if (parsed) {
    if (_answerCache.size >= _ANSWER_CACHE_MAX) _answerCache.delete(_answerCache.keys().next().value);
    _answerCache.set(_ck, result);
  }
  onStage('done');
  return result;
}
